// @ts-nocheck
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface ManualRegistrationFormProps {
  eventId: string;
  organizationId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const ManualRegistrationForm: React.FC<ManualRegistrationFormProps> = ({
  eventId,
  organizationId,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    athleteName: '',
    athleteEmail: '',
    ageGroup: '',
    registrationType: 'individual',
    paymentStatus: 'paid',
    totalAmount: '',
    coachName: '',
    coachEmail: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.athleteName || !formData.athleteEmail) {
      toast.error('Athlete name and email are required');
      return;
    }

    setLoading(true);

    try {
      // Create or find athlete profile
      let athleteProfile = null;
      
      // Check if profile exists with this email
      const { data: existingProfile, error: profileSearchError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', formData.athleteEmail)
        .maybeSingle();

      if (profileSearchError) throw profileSearchError;

      if (existingProfile) {
        athleteProfile = existingProfile;
      } else {
        // Create new profile (without auth account - manual registration)
        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert({
            email: formData.athleteEmail,
            name: formData.athleteName,
            role: 'athlete',
            organization_id: organizationId,
          })
          .select()
          .single();

        if (profileError) throw profileError;
        athleteProfile = newProfile;
      }

      // Create registration
      const { data: registration, error: registrationError } = await supabase
        .from('event_registrations')
        .insert({
          event_id: eventId,
          profile_id: athleteProfile.id,
          registration_type: formData.registrationType,
          payment_status: formData.paymentStatus,
          total_amount: parseFloat(formData.totalAmount) || 0,
          organization_id: organizationId,
        })
        .select()
        .single();

      if (registrationError) throw registrationError;

      // If coach email provided, send invitation
      if (formData.coachEmail && formData.coachName) {
        const { error: inviteError } = await supabase
          .from('coach_invitations')
          .insert({
            event_id: eventId,
            coach_email: formData.coachEmail,
            coach_name: formData.coachName,
            status: 'pending',
            invited_by_org_id: organizationId,
          });

        if (inviteError) {
          console.error('Error creating coach invitation:', inviteError);
          toast.warning('Registration created but coach invitation failed');
        }

        // TODO: Trigger email to coach
        // Call edge function to send coach invitation email
      }

      onSuccess();
    } catch (error: any) {
      console.error('Error creating registration:', error);
      toast.error(error.message || 'Failed to create registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Athlete Information */}
      <div className="space-y-4 p-4 border rounded-lg">
        <h3 className="font-semibold">Athlete Information</h3>
        
        <div className="space-y-2">
          <Label htmlFor="athleteName">Athlete Name *</Label>
          <Input
            id="athleteName"
            value={formData.athleteName}
            onChange={(e) => setFormData({ ...formData, athleteName: e.target.value })}
            placeholder="John Doe"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="athleteEmail">Athlete Email *</Label>
          <Input
            id="athleteEmail"
            type="email"
            value={formData.athleteEmail}
            onChange={(e) => setFormData({ ...formData, athleteEmail: e.target.value })}
            placeholder="athlete@example.com"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ageGroup">Age Group</Label>
          <Input
            id="ageGroup"
            value={formData.ageGroup}
            onChange={(e) => setFormData({ ...formData, ageGroup: e.target.value })}
            placeholder="U16, U18, etc."
          />
        </div>
      </div>

      {/* Registration Details */}
      <div className="space-y-4 p-4 border rounded-lg">
        <h3 className="font-semibold">Registration Details</h3>

        <div className="space-y-2">
          <Label htmlFor="registrationType">Registration Type</Label>
          <Select
            value={formData.registrationType}
            onValueChange={(value) => setFormData({ ...formData, registrationType: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="individual">Individual</SelectItem>
              <SelectItem value="team">Team</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="totalAmount">Total Amount ($)</Label>
          <Input
            id="totalAmount"
            type="number"
            step="0.01"
            value={formData.totalAmount}
            onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
            placeholder="0.00"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="paymentStatus">Payment Status</Label>
          <Select
            value={formData.paymentStatus}
            onValueChange={(value) => setFormData({ ...formData, paymentStatus: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Optional Coach Information */}
      <div className="space-y-4 p-4 border rounded-lg">
        <h3 className="font-semibold">Coach Information (Optional)</h3>
        <p className="text-sm text-muted-foreground">
          If provided, an invitation will be sent to this coach
        </p>

        <div className="space-y-2">
          <Label htmlFor="coachName">Coach Name</Label>
          <Input
            id="coachName"
            value={formData.coachName}
            onChange={(e) => setFormData({ ...formData, coachName: e.target.value })}
            placeholder="Jane Smith"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="coachEmail">Coach Email</Label>
          <Input
            id="coachEmail"
            type="email"
            value={formData.coachEmail}
            onChange={(e) => setFormData({ ...formData, coachEmail: e.target.value })}
            placeholder="coach@example.com"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Registration
        </Button>
      </div>
    </form>
  );
};

export default ManualRegistrationForm;